import {ReactNode} from "react";
import WindowOutlinedIcon from "@mui/icons-material/WindowOutlined";
import CropSquareIcon from "@mui/icons-material/CropSquare";
import VerticalSplitIcon from "@mui/icons-material/VerticalSplit";
import HorizontalSplitIcon from "@mui/icons-material/HorizontalSplit";
import ViewWeekIcon from "@mui/icons-material/ViewWeek";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
    CenterGlazedIcon,
    DoubleGlazedIcon,
    FlushGlazedInsideIcon,
    FlushGlazedOutsideIcon,
    GlazedInsideIcon,
    GlazedOutsideIcon,
    PanelCenterIcon,
    PanelInsideIcon,
    PanelOutsideIcon,
} from "./FillIcons";
import {ElevationApp} from "../../ElevationApp";
import {MdSelection} from "../../model/view/MdViewSettings";
import {RibbonButton, RibbonGroup, RibbonIconButton, RibbonIconGrid, RibbonSelect} from "../Ribbon";
import {
    convertItem,
    distributeCells,
    findItem,
    getItemProfile,
    setCellGlass,
    setCellGlazing,
    setCellPanelMaterial,
    setCellPanelPosition,
    setItemProfile,
    splitItem,
    makeDoorSlot,
    moveDoorSlot,
} from "../../scenes/frameGrid/BlockOps";
import SensorDoorOutlinedIcon from "@mui/icons-material/SensorDoorOutlined";
import {frameProfileLabels, MdFrameProfileType} from "../../model/frame/MdFrameSpec";
import {
    glazingLabels,
    itemGlazing,
    itemPanelMaterial,
    itemPanelPosition,
    MdGlazing,
    MdPanelMaterial,
    MdPanelPosition,
    panelMaterialLabels,
    panelPositionLabels,
} from "../../model/frame/MdFrameBlock";
import {liteGlass, MdGlass, withGlassEdit} from "../../model/glass/MdGlass";
import {anyBeadConflict, GlassControls, sharedGlass} from "./GlassControls";
import {supportedProfiles} from "../../scenes/frameProfile/Helpers";
import {useEventEmitter2} from "../../util/useEventEmitter2";
import {MenubarShortcut, useMenubarShortcuts} from "../useMenubarShortcuts";
import {deselect, emitChanged, emitRebuild, remove} from "./helpers";

type CellSel = Extract<MdSelection, {type: "lite" | "panel"}>;

// "inherit" clears the per-item override so the cell follows MdFrameSpec.profile.
const INHERIT = "inherit";
const PROFILE_OPTIONS = [
    {value: INHERIT, label: "Default (Spec)"},
    ...supportedProfiles.map(p => ({value: p as string, label: frameProfileLabels[p]})),
];

// the glazing options, in ribbon order.
const GLAZING_OPTIONS: {value: MdGlazing; icon: ReactNode}[] = [
    {value: "inside", icon: <GlazedInsideIcon />},
    {value: "outside", icon: <GlazedOutsideIcon />},
    {value: "double", icon: <DoubleGlazedIcon />},
    {value: "center", icon: <CenterGlazedIcon />},
    {value: "flushInside", icon: <FlushGlazedInsideIcon />},
    {value: "flushOutside", icon: <FlushGlazedOutsideIcon />},
];

// the fixed-panel positions, in ribbon order.
const POSITION_OPTIONS: {value: MdPanelPosition; icon: ReactNode}[] = [
    {value: "inside", icon: <PanelInsideIcon />},
    {value: "outside", icon: <PanelOutsideIcon />},
    {value: "center", icon: <PanelCenterIcon />},
];

const MATERIAL_OPTIONS = (["metal", "wood"] as MdPanelMaterial[])
    .map(m => ({value: m as string, label: panelMaterialLabels[m]}));

export function CellSelection(props: {app: ElevationApp; selection: CellSel[]}) {
    const app = props.app;
    const sel = props.selection;
    const multi = sel.length > 1;
    const ids = sel.map(s => s.id);

    // re-render on model edits so the Profile dropdown tracks the current override.
    useEventEmitter2(app.frameSpec.frameBlocksChanged);

    // the Fill toggle reflects the kinds in the selection: active only when every
    // selected cell is already that kind.
    const allLite = sel.every(s => s.type === "lite");
    const allPanel = sel.every(s => s.type === "panel");

    // convert every selected cell, then re-emit the selection reflecting the new
    // kinds so the tab (and the Fill toggle) stay in sync.
    const setKind = (toKind: "lite" | "fixedPanel") => {
        const asType = toKind === "fixedPanel" ? "panel" : "lite";
        let changed = false;
        for(const s of sel) {
            if(s.type === asType) continue; // already this kind
            if(convertItem(app.frameSpec, s.id, toKind)) changed = true;
        }
        if(!changed) return;

        emitChanged(app);
        app.viewSettings.selection.emit(sel.map(s => ({type: asType, id: s.id, size: s.size})));
    };

    // split is single-cell only (it divides one footprint), so it's disabled while
    // multiple cells are selected.
    const split = (direction: "vertical" | "horizontal") => {
        if(multi) return;
        if(splitItem(app.frameSpec, sel[0].id, direction, sel[0].size)) {
            emitChanged(app);
        }
    };

    // equalize the selected sibling cells' sizes (per contiguous run within a
    // row/column). Enabled only for a multi-selection.
    const distribute = () => {
        if(!multi) return;
        if(distributeCells(app.frameSpec, ids)) emitChanged(app);
    };

    // turn the cell into a new door slot (multi-opening screens) and select it.
    const makeSlot = () => {
        if(multi) return;
        if(!makeDoorSlot(app.frameSpec, sel[0].id)) {
            app.draw.showError("Can't make a door slot here");
            return;
        }

        emitRebuild(app);
        deselect(app);
    };

    // move a door slot into this cell; the slot's old cell becomes glass.
    const moveSlot = (name: string) => {
        if(multi || !name) return;
        if(!moveDoorSlot(app.frameSpec, name, sel[0].id)) {
            app.draw.showError("Can't move the door slot here");
            return;
        }

        emitRebuild(app);
        deselect(app);
    };

    // a secondary opening's frame is edited on its primary.
    const canSlot = !multi && !app.secondaryOf.value;
    const slotNames = app.frameSpec.slots.map(s => s.name);

    // remove every selected cell.
    const removeAll = () => {
        for(const id of ids) remove(app, id);
    };

    // the value shared by every selected cell, or undefined when they disagree —
    // drives the exclusive fill-configuration toggles.
    const sharedValue = <T,>(read: (id: string) => T | undefined): T | undefined => {
        const values = ids.map(read);
        return values.every(v => v === values[0]) ? values[0] : undefined;
    };

    const glazing = sharedValue(id => {
        const item = findItem(app.frameSpec, id);
        return item && itemGlazing(item);
    });

    const material = sharedValue(id => {
        const item = findItem(app.frameSpec, id);
        return item && itemPanelMaterial(item);
    });

    const position = sharedValue(id => {
        const item = findItem(app.frameSpec, id);
        return item && itemPanelPosition(item);
    });

    // the glass of every selected lite, for the Glass group's shared values.
    const glassList = ids
        .map(id => findItem(app.frameSpec, id))
        .filter(item => item?.kind === "lite")
        .map(item => liteGlass(item!));

    // apply one glass edit to every selected lite; each lite runs the thermal rules
    // against its own record.
    const editGlass = (patch: MdGlass) => {
        applyToSelection(id => {
            const item = findItem(app.frameSpec, id);
            if(item?.kind !== "lite") return false;
            return setCellGlass(app.frameSpec, id, withGlassEdit(item.glass, patch));
        });
    };

    // the glazing / material / position setters each fold over the selection; the
    // profile a configuration forces is applied by the BlockOps setter.
    const applyToSelection = (set: (id: string) => boolean) => {
        let changed = false;
        for(const id of ids) {
            if(set(id)) changed = true;
        }
        if(changed) emitChanged(app);
    };

    // the current per-item profile override across the selection: the shared value
    // when they all agree (undefined override shows as "inherit"), else "" so the
    // dropdown reads as mixed/blank.
    const overrides = ids.map(id => getItemProfile(app.frameSpec, id));
    const profileValue = overrides.every(o => o === overrides[0]) ? (overrides[0] ?? INHERIT) : "";

    // override (or inherit, when "inherit") the frame profile on every selected cell.
    const setProfile = (value: string) => {
        const profile = value === INHERIT ? undefined : (value as MdFrameProfileType);
        let changed = false;
        for(const id of ids) {
            if(setItemProfile(app.frameSpec, id, profile)) changed = true;
        }
        if(changed) emitChanged(app);
    };

    // one descriptor per keyboard action. G/F and Delete apply to the whole
    // selection; the single-cell V/H splits are only registered when one cell is
    // selected so they don't fire (disabled) during a multi-selection.
    const keys = {
        lite: {keys: ["g"], display: "G", hint: "Press G or F to fill with glass or a fixed panel", run: () => setKind("lite")},
        fixedPanel: {keys: ["f"], display: "F", hint: "Press G or F to fill with glass or a fixed panel", run: () => setKind("fixedPanel")},
        splitV: {keys: ["v"], display: "V", hint: "Press V or H to split", run: () => split("vertical")},
        splitH: {keys: ["h"], display: "H", hint: "Press V or H to split", run: () => split("horizontal")},
        remove: {keys: ["Delete", "Backspace"], display: "Delete", hint: "Press Delete to remove", run: removeAll},
    } satisfies Record<string, MenubarShortcut>;
    useMenubarShortcuts([
        keys.lite, keys.fixedPanel, keys.remove,
        ...(multi ? [] : [keys.splitV, keys.splitH]),
    ]);

    return (
        <>
            <RibbonGroup label="Fill">
                <RibbonButton
                    icon={<WindowOutlinedIcon />}
                    label="Glass"
                    active={allLite}
                    shortcut={keys.lite.display}
                    onClick={keys.lite.run}
                />
                <RibbonButton
                    icon={<CropSquareIcon />}
                    label="Fixed Panel"
                    active={allPanel}
                    shortcut={keys.fixedPanel.display}
                    onClick={keys.fixedPanel.run}
                />
            </RibbonGroup>

            {/* the fill configuration of whichever kind fills every selected cell.
                Center / Flush glazing and a centered panel force the frame profile
                they need onto the cell and the sticks around it. */}
            {allLite && (
                <>
                    <RibbonGroup label="Glazing">
                        <RibbonIconGrid columns={3}>
                            {GLAZING_OPTIONS.map(o => (
                                <RibbonIconButton
                                    key={o.value}
                                    icon={o.icon}
                                    label={glazingLabels[o.value]}
                                    active={glazing === o.value}
                                    onClick={() => applyToSelection(id => setCellGlazing(app.frameSpec, id, o.value))}
                                />
                            ))}
                        </RibbonIconGrid>
                    </RibbonGroup>

                    <GlassControls
                        app={app}
                        glass={sharedGlass(glassList)}
                        beadConflict={anyBeadConflict(glassList)}
                        onChange={editGlass}
                    />
                </>
            )}

            {allPanel && (
                <>
                    <RibbonGroup label="Panel Material">
                        <RibbonSelect
                            value={material ?? ""}
                            options={MATERIAL_OPTIONS}
                            onChange={value => applyToSelection(id => setCellPanelMaterial(app.frameSpec, id, value as MdPanelMaterial))}
                        />
                    </RibbonGroup>

                    <RibbonGroup label="Panel Position">
                        {POSITION_OPTIONS.map(o => (
                            <RibbonButton
                                key={o.value}
                                icon={o.icon}
                                label={panelPositionLabels[o.value]}
                                active={position === o.value}
                                onClick={() => applyToSelection(id => setCellPanelPosition(app.frameSpec, id, o.value))}
                            />
                        ))}
                    </RibbonGroup>
                </>
            )}

            <RibbonGroup label="Actions">
                <RibbonButton
                    icon={<VerticalSplitIcon />}
                    label="Split Vertical"
                    disabled={multi}
                    shortcut={keys.splitV.display}
                    onClick={keys.splitV.run}
                />
                <RibbonButton
                    icon={<HorizontalSplitIcon />}
                    label="Split Horizontal"
                    disabled={multi}
                    shortcut={keys.splitH.display}
                    onClick={keys.splitH.run}
                />
                <RibbonButton
                    icon={<DeleteOutlineIcon sx={{color: "error.main"}} />}
                    label="Remove"
                    shortcut={keys.remove.display}
                    onClick={keys.remove.run}
                />
            </RibbonGroup>

            <RibbonGroup label="Arrange">
                <RibbonButton
                    icon={<ViewWeekIcon />}
                    label="Distribute"
                    disabled={!multi}
                    onClick={distribute}
                />
            </RibbonGroup>

            <RibbonGroup label="Profile">
                <RibbonSelect
                    value={profileValue}
                    options={PROFILE_OPTIONS}
                    onChange={setProfile}
                />
            </RibbonGroup>

            <RibbonGroup label="Door Slot">
                <RibbonButton
                    icon={<SensorDoorOutlinedIcon />}
                    label="Make Door Slot"
                    disabled={!canSlot}
                    onClick={makeSlot}
                />
                {slotNames.length > 0 && (
                    <RibbonSelect
                        value=""
                        options={[{value: "", label: "Move slot here…"}, ...slotNames.map(n => ({value: n, label: `Slot ${n}`}))]}
                        disabled={!canSlot}
                        onChange={moveSlot}
                    />
                )}
            </RibbonGroup>
        </>
    );
}
